import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import type { SxProps } from '@mui/system';
import type { Modifier, State } from '@popperjs/core';
import type { ButtonActions } from 'common/components/Elements/Button/Button';
import Button from 'common/components/Elements/Button/Button';
import Icon from 'common/components/Elements/Icon/Icon';
import useDisableScrollOnKeyboardEvent from 'common/utilities/hooks/useDisableScrollOnKeyboardEvent/useDisableScrollOnKeyboardEvent';
import useShortcut from 'common/utilities/hooks/useShortcut/useShortcut';
import useUpDownList from 'common/utilities/hooks/useUpDownList/useUpDownList';
import * as I from 'core/api/coreApiClient';
import { isCommandForReactRouter } from 'core/utilities/command/command';
import type { EnhancedCommand } from 'core/utilities/core.types';
import theme, { ThemeColors } from 'core/utilities/theme';
import maxSize from 'popper-max-size-modifier';
import type {
CSSProperties,   RefObject} from 'react';
import React, { useCallback,
useEffect, useState, } from 'react';

import ItemButton from './ItemButton/ItemButton';
import ItemExternalLink from './ItemExternalLink/ItemExternalLink';
import ItemFileDownload from './ItemFileDownload/ItemFileDownload';
import ItemGet from './ItemGet/ItemGet';
import ItemGroupMenu from './ItemGroupMenu/ItemGroupMenu';
import ItemLink from './ItemLink/ItemLink';
import ItemNestedMenu from './ItemNestedMenu/ItemNestedMenu';
import ItemOnClick from './ItemOnClick/ItemOnClick';
import ItemPersist from './ItemPersist/ItemPersist';

export const menuItemStyles: SxProps = {
  paddingRight: '24px',
  width: '100%',
};

// Add scrollbar to menu or sub-menu higher than the viewport
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyMaxSize: Modifier<'applyMaxSize', any> = {
  name: 'applyMaxSize',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['maxSize'],
  fn: ({ state }: { state: State }) => {
    const { height } = state.modifiersData.maxSize;

     
    state.styles.popper.maxHeight = `${height as number}px`;
     
    state.styles.popper.overflowY = 'auto';
  },
};

export const popperjsMaxSizeModifiers = [maxSize, applyMaxSize];

export type CloseEvent = React.MouseEvent | React.TouchEvent;
type ToggleEvent = React.MouseEvent<HTMLButtonElement, MouseEvent>;

export const getDropdownItemForCommand = (
  item: EnhancedCommand,
  selected: boolean,
  buttonRef: React.RefObject<HTMLElement>,
  handleClose: (event: CloseEvent, ref: RefObject<HTMLElement>) => void,
  handleToggle: (event?: ToggleEvent) => void,
  handleNestedMenuToggle: (toggle: boolean) => void,
) => {
  const {
    id, commands, label, commandType, httpVerb, onClick,
  } = item;

  // Logic below is similar to the Command component's render method.
  // Please read the information present in the Command component to get
  // more information about the different types of commands.

  if (commandType === I.CommandTypeConstants.Separator) {
    return (<Divider data-testid="divider" key={id} style={{ margin: theme.spacing(1, 0) }} />);
  }

  if (commandType === I.CommandTypeConstants.RESTResource) {
    if (httpVerb === I.HttpVerbConstants.POST) {
      return (
        <ItemPersist
          key={item.id || item.label}
          item={item}
          buttonRef={buttonRef}
          onClose={handleClose}
          selected={selected}
        />
      );
    }

    if (httpVerb === I.HttpVerbConstants.GET) {
      return (
        <ItemGet
          key={item.id || item.label}
          item={item}
          buttonRef={buttonRef}
          onClose={handleClose}
          selected={selected}
        />
      );
    }
  }

  if (commandType === I.CommandTypeConstants.FileDownload) {
    return (
      <ItemFileDownload
        key={item.id || item.label}
        item={item}
        buttonRef={buttonRef}
        onClose={handleClose}
        selected={selected}
      />
    );
  }

  // ExternalHREF, FileDownload, JSFHREF
  if (!isCommandForReactRouter(item)) {
    return (
      <ItemExternalLink
        key={item.id || item.label}
        item={item}
        buttonRef={buttonRef}
        onClose={handleClose}
        selected={selected}
      />
    );
  }

  if (!commandType && commands.length > 0) {
    if (label) {
      // Nested dropdown
      return (
        <ItemNestedMenu
          key={item.id || item.label}
          onClickMenuItem={handleToggle}
          item={item}
          buttonRef={buttonRef}
          selected={selected}
          onNestedMenuToggle={handleNestedMenuToggle}
        />
      );
    }
    // Menu items next to each other
    return (
      <ItemGroupMenu
        key={item.id || item.label}
        item={item}
        buttonRef={buttonRef}
        onClose={handleClose}
      />
    );
  }

  if (onClick) {
    // Item with 'onClick' callback (no url)
    return (
      <ItemOnClick
        key={item.id || item.label}
        item={item}
        buttonRef={buttonRef}
        onClose={handleClose}
        selected={selected}
      />
    );
  }

  if (commandType === I.CommandTypeConstants.HREF) {
    return (
      <ItemLink
        key={item.id || item.label}
        item={item}
        buttonRef={buttonRef}
        onClose={handleClose}
        selected={selected}
      />
    );
  }

  return (
    <ItemButton
      key={item.id || item.label || item.title}
      item={item}
      buttonRef={buttonRef}
      onClose={handleClose}
      selected={selected}
    />
  );
};

export interface ItemComponentProps {
  item: EnhancedCommand;
  buttonRef: React.RefObject<HTMLElement>;
  onClose: (event: CloseEvent, ref: React.RefObject<HTMLElement>) => void;
  selected?: boolean;
  autostart?: React.MouseEvent<HTMLElement>;
}

export interface ComponentProps {
  items: EnhancedCommand[];
  itemsAnchor?: 'left' | 'right';
  id: string;
  label?: string | JSX.Element;
  buttonSize?: 'small' | 'medium';
  action: ButtonActions;
  inheritColor?: boolean;
  title?: string;
  disabled?: boolean;
  paddingTop?: number;
  arrowIcon?: JSX.Element;
  hideArrowIcon?: boolean;
}

// The DropDown component shouldn't be used anywhere other than within the Command component
function DropDown(props: ComponentProps) {
  const buttonRef = React.createRef<HTMLButtonElement>();
  const menuListRef = React.createRef<HTMLUListElement>();
  const [toggle, setToggle] = useState(false);
  const [nestedMenuToggle, setNestedMenuToggle] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLButtonElement>(null);

  const {
    items,
    itemsAnchor,
    id,
    label,
    buttonSize,
    action,
    inheritColor,
    title,
    disabled,
    paddingTop,
    arrowIcon,
    hideArrowIcon,
  } = props;

  const { active: selectedItem, reset: resetSelectedItem } = useUpDownList(
    items.length,
    !toggle || nestedMenuToggle,
    items.map((item, i) => (!item.active ? i : undefined)).filter((item) => item !== undefined) as number[],
  );

  const selectItemTrigger = useShortcut('Enter', true, { disabled: !toggle || nestedMenuToggle, debounceTime: 0 });

  useEffect(() => {
    if (selectItemTrigger && selectedItem !== -1) {
      (buttonRef?.current as HTMLButtonElement).blur();

      const listItem = menuListRef.current
        ?.querySelector(`li:nth-of-type(${selectedItem + 1})`) as HTMLElement;

      // Hover the item (used to trigger nested dropdown)
      const mouseoverEvent = new MouseEvent('mouseover', { bubbles: true });
      listItem.dispatchEvent(mouseoverEvent);

      // Click the item
      (listItem.querySelector(':first-child') as HTMLElement)
        ?.click();
    }
  }, [buttonRef, menuListRef, selectItemTrigger, selectedItem]);

  const handleClose = useCallback(() => {
    setToggle(false);
    setNestedMenuToggle(false);
    resetSelectedItem();
  }, [resetSelectedItem]);

  const handleCloseEvent = (event: CloseEvent, ref: RefObject<HTMLElement>) => {
    if (ref.current && ref.current.contains(event.target as HTMLElement)) {
      return;
    }

    handleClose();
  };

  const handleToggle = (event?: ToggleEvent) => {
    if (!anchorEl) setAnchorEl(buttonRef.current);

    // The dropdown label might be an input (for example checkbox)
    // we don't trigger the dropdown toggle when the user click on it
    // only if he clicks on the text, arrow icon etc.
    if (!event || (event && (event.target as HTMLElement).tagName.toUpperCase() !== 'INPUT')) {
      if (toggle) {
        handleClose();
      } else {
        setToggle(true);
      }
    }
  };

  const handleNestedMenuToggle = (newNestedMenuToggle: boolean) => {
    setNestedMenuToggle(newNestedMenuToggle);
  };

  /** Disabled when closed or when a nested menu is open */
  const escapeShortcutTrigger = useShortcut('Escape', true, { disabled: !toggle || nestedMenuToggle });

  useEffect(() => {
    if (escapeShortcutTrigger) {
      handleClose();
    }
  }, [escapeShortcutTrigger, handleClose]);

  /** Close the dropdown when the user change dropdown focus using the tab keystroke */
  const tabTrigger = useShortcut('Tab', true, { disabled: !toggle, debounceTime: 0 });

  useEffect(() => {
    if (tabTrigger) {
      handleClose();
    }
  }, [tabTrigger, handleClose]);

  const menuItems = items.map((item, i) => getDropdownItemForCommand(
    item, selectedItem === i, buttonRef, handleCloseEvent, handleToggle, handleNestedMenuToggle,
  ));

  useDisableScrollOnKeyboardEvent(!toggle);

  const dropdownStyle: CSSProperties = {
    zIndex: 1301, // above Drawer zIndex
    overflowX: 'hidden',
  };

  if (paddingTop) dropdownStyle.paddingTop = `${paddingTop}px`;

  return (
    <>
      <Button
        inheritColor={inheritColor}
        action={action}
        buttonRef={buttonRef}
        onClick={(e) => handleToggle(e)}
        e2eId={id}
        small={buttonSize !== 'medium'}
        title={title}
        buttonIcon={!label || hideArrowIcon}
        disabled={disabled}
        ariaHaspopup
        ariaExpanded={toggle}
      >
        {label}
        {hideArrowIcon !== true && (arrowIcon || (
          <Icon
            element={KeyboardArrowDownIcon}
            // Compensate for the SVG margin, otherwise the dropdown has too much spacing
            // on the right compare to buttons/links
            style={{ marginRight: label ? theme.spacing(-0.5) : '-1px' }}
          />
        ))}
      </Button>
      {toggle && (
        <Popper
          open
          placement={itemsAnchor === 'right' ? 'bottom-end' : 'bottom-start'}
          anchorEl={anchorEl}
          style={dropdownStyle}
          data-e2e-dropdown-items=""
          modifiers={[
            {
              name: 'computeStyles',
              options: {
                gpuAcceleration: false, // needed to avoid blurry content when zooming with the browser
              },
            },
            {
              name: 'offset',
              options: {
                offset: [-8, 0],
              },
            },
            ...popperjsMaxSizeModifiers,
          ]}
          // Used by the Commands component in the responsive logic
          className="dropDownItems"
        >
          <Paper style={{ background: ThemeColors.dropdownMenuBg, borderColor: ThemeColors.dropdownMenuBorder }}>
            <ClickAwayListener onClickAway={(e) => handleCloseEvent(e as unknown as CloseEvent, buttonRef)}>
              <MenuList dense ref={menuListRef}>
                {menuItems}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Popper>
      )}
    </>
  );
}

export default DropDown;
